import React, { useState } from 'react';
import { ChevronDown, ChevronUp, ArrowUpDown, Plus, Crown, DollarSign } from 'lucide-react';
import { useAppSelector } from '../../store/hooks';
import { AdminHostBalance } from '../../services/billingService';
import Button from '../UI/Button';
import AdminBalanceRowDetail from './AdminBalanceRowDetail';
import clsx from 'clsx';

interface AdminBalanceTableProps {
  balances: AdminHostBalance[];
  loading?: boolean;
  onTopUp: (balance: AdminHostBalance) => void;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (field: string) => void;
  onBalanceChange?: () => void; // Callback when balance changes (e.g., after adding extra service)
}

const AdminBalanceTable: React.FC<AdminBalanceTableProps> = ({
  balances,
  loading = false,
  onTopUp,
  sortBy = 'host_name',
  sortOrder = 'asc',
  onSort,
  onBalanceChange,
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const toggleRow = (id: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const formatBalance = (balance: string): string => {
    const num = parseFloat(balance);
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
    return `${Math.floor(diffDays / 365)}y ago`;
  };

  const getBalanceColor = (balance: string): string => {
    const num = parseFloat(balance);
    if (num > 0) return 'text-green-600 dark:text-green-400';
    if (num < 0) return 'text-red-600 dark:text-red-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  const SortableHeader: React.FC<{ field: string; label: string; currentSort?: string; currentOrder?: 'asc' | 'desc' }> = ({
    field,
    label,
    currentSort,
    currentOrder,
  }) => {
    const isActive = currentSort === field;
    return (
      <th
        className={`px-4 py-3 text-center text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-50 ${
          theme === 'dark'
            ? 'text-gray-300 bg-gray-700 hover:bg-gray-600'
            : 'text-gray-500 bg-gray-50 hover:bg-gray-100'
        }`}
        onClick={() => onSort?.(field)}
      >
        <div className="flex items-center justify-center gap-2">
          <span>{label}</span>
          <ArrowUpDown className={`h-4 w-4 ${isActive ? 'opacity-100' : 'opacity-30'}`} />
          {isActive && (
            <span className="text-xs">
              {currentOrder === 'asc' ? '↑' : '↓'}
            </span>
          )}
        </div>
      </th>
    );
  };

  if (loading) {
    return (
      <div className={`rounded-lg border overflow-hidden ${
        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mirsal-green mx-auto"></div>
          <p className={`mt-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Loading balances...
          </p>
        </div>
      </div>
    );
  }

  if (balances.length === 0) {
    return (
      <div className={`rounded-lg border p-12 text-center ${
        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <p className={`text-lg font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          No hosts found
        </p>
        <p className={`text-sm mt-2 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
          Try adjusting your filters
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Desktop Table View (md and above) */}
      <div className={`hidden md:block rounded-lg border overflow-hidden ${
        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead>
            <tr>
              <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider w-12">
                {/* Expand/Collapse column */}
              </th>
              <SortableHeader field="host_name" label="Host Name" currentSort={sortBy} currentOrder={sortOrder} />
              <th className={`px-4 py-3 text-center text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-300 bg-gray-700' : 'text-gray-500 bg-gray-50'
              }`}>
                Contact
              </th>
              <SortableHeader field="balance" label="Balance" currentSort={sortBy} currentOrder={sortOrder} />
              <SortableHeader field="event_count" label="Events" currentSort={sortBy} currentOrder={sortOrder} />
              <SortableHeader field="transaction_count" label="Transactions" currentSort={sortBy} currentOrder={sortOrder} />
              <SortableHeader field="last_transaction_date" label="Last Transaction" currentSort={sortBy} currentOrder={sortOrder} />
              <th className={`px-4 py-3 text-center text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-300 bg-gray-700' : 'text-gray-500 bg-gray-50'
              }`}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody className={`divide-y ${theme === 'dark' ? 'divide-gray-700 bg-gray-800' : 'divide-gray-200 bg-white'}`}>
            {balances.map((balance) => {
              const isExpanded = expandedRows.has(balance.id);
              return (
                <React.Fragment key={balance.id}>
                  <tr
                    className={clsx(
                      'cursor-pointer transition-colors',
                      theme === 'dark'
                        ? 'hover:bg-gray-700'
                        : 'hover:bg-gray-50',
                      isExpanded && (theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50')
                    )}
                    onClick={() => toggleRow(balance.id)}
                  >
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      {isExpanded ? (
                        <ChevronUp className={`h-5 w-5 mx-auto ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} />
                      ) : (
                        <ChevronDown className={`h-5 w-5 mx-auto ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} />
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-2">
                        <span className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {balance.host_name}
                        </span>
                        {balance.is_mirsal_member && (
                          <div className="relative group">
                            <Crown className="h-4 w-4 text-yellow-500" />
                            <span className={`absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 ${
                              theme === 'dark' ? 'bg-gray-700 text-gray-200' : 'bg-gray-900 text-white'
                            }`}>
                              Mirsal Member
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <div className="text-sm">
                        {balance.host_phone && (
                          <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
                            {balance.host_phone}
                          </p>
                        )}
                        {balance.host_email && (
                          <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                            {balance.host_email}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <span className={`text-sm font-semibold ${getBalanceColor(balance.balance)}`}>
                        {formatBalance(balance.balance)} JOD
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        {balance.event_count}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        {balance.transaction_count}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        {formatDate(balance.last_transaction_date)}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => onTopUp(balance)}
                        className="flex items-center gap-1"
                      >
                        <Plus className="h-3 w-3" />
                        <span>Top Up</span>
                      </Button>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={8} className="px-0 py-0">
                        <AdminBalanceRowDetail balanceId={balance.id} onBalanceChange={onBalanceChange} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      {/* Mobile Card View (below md) */}
      <div className="md:hidden space-y-3">
        {balances.map((balance) => {
          const isExpanded = expandedRows.has(balance.id);
          return (
            <div
              key={balance.id}
              className={`rounded-lg border overflow-hidden transition-all ${
                theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              }`}
            >
              {/* Card Header */}
              <div
                onClick={() => toggleRow(balance.id)}
                className={clsx(
                  'p-4 cursor-pointer transition-colors',
                  theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50',
                  isExpanded && (theme === 'dark' ? 'bg-gray-700 border-b border-gray-700' : 'bg-gray-50 border-b border-gray-200')
                )}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className={`text-base font-semibold truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {balance.host_name}
                    </span>
                    {balance.is_mirsal_member && (
                      <div className="relative group flex-shrink-0">
                        <Crown className="h-4 w-4 text-yellow-500" />
                        <span className={`absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 ${
                          theme === 'dark' ? 'bg-gray-700 text-gray-200' : 'bg-gray-900 text-white'
                        }`}>
                          Mirsal Member
                        </span>
                      </div>
                    )}
                  </div>
                  {isExpanded ? (
                    <ChevronUp className={`h-5 w-5 flex-shrink-0 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} />
                  ) : (
                    <ChevronDown className={`h-5 w-5 flex-shrink-0 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} />
                  )}
                </div>
                
                {/* Key Info Grid */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Balance</p>
                    <p className={`text-lg font-semibold ${getBalanceColor(balance.balance)}`}>
                      {formatBalance(balance.balance)} JOD
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Events</p>
                    <p className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {balance.event_count}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Transactions</p>
                    <p className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {balance.transaction_count}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Last Transaction</p>
                    <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      {formatDate(balance.last_transaction_date)}
                    </p>
                  </div>
                </div>

                {/* Contact Info */}
                {(balance.host_phone || balance.host_email) && (
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                    {balance.host_phone && (
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                        {balance.host_phone}
                      </p>
                    )}
                    {balance.host_email && (
                      <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        {balance.host_email}
                      </p>
                    )}
                  </div>
                )}

                {/* Top Up Button */}
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onTopUp(balance);
                    }}
                    className="w-full flex items-center justify-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Top Up Balance</span>
                  </Button>
                </div>
              </div>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="border-t border-gray-200 dark:border-gray-700">
                  <AdminBalanceRowDetail balanceId={balance.id} onBalanceChange={onBalanceChange} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default AdminBalanceTable;

